import ConfirmDialog from '@/components/ConfirmDialog';
import SortableHeader from '@/components/SortableHeader';
import TablePagination from '@/components/TablePagination';
import { useToast } from '@/hooks/useToast';
import { templatesService } from '@/services/templatesService';
import {
  CATEGORY_COLORS,
  STATUS_COLORS,
  TEMPLATE_CATEGORIES,
  TEMPLATE_STATUSES,
} from '@/types/templates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  Layout,
  Pause,
  Plus,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

/**
 * Templates list page with pagination, filtering, and search
 * @returns {JSX.Element} TemplatesList component
 */
const TemplatesList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // State for filters and pagination
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    pageIndex: 0,
    pageSize: 10,
  });

  // Modal states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Fetch templates with current filters
  const {
    data: templatesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['templates', projectId, filters],
    queryFn: () =>
      templatesService.getTemplates(projectId, {
        category: filters.category,
        status: filters.status,
        search: filters.search,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        page: filters.pageIndex + 1,
        limit: filters.pageSize,
      }),
    enabled: !!projectId,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: ({ templateId }) =>
      templatesService.deleteTemplate(projectId, templateId),
    onSuccess: () => {
      showSuccess('Template deleted successfully');
      queryClient.invalidateQueries(['templates', projectId]);
    },
    onError: error => {
      showError(error.message || 'Failed to delete template');
    },
  });

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      pageIndex: 0, // Reset to first page when filters change
    }));
  };

  // Handle search change
  const handleSearchChange = e => {
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      search: value,
      pageIndex: 0,
    }));
  };

  // Handle pagination
  const handlePageChange = pageIndex => {
    setFilters(prev => ({ ...prev, pageIndex }));
  };

  const handlePageSizeChange = pageSize => {
    setFilters(prev => ({ ...prev, pageSize, pageIndex: 0 }));
  };

  // Handle sorting
  const handleSort = sortBy => {
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === 'asc' ? 'desc' : 'asc',
      pageIndex: 0,
    }));
  };

  // Handle delete template
  const handleDeleteTemplate = async (e, template) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setShowDeleteDialog(true);
  };

  const confirmDeleteTemplate = async () => {
    if (templateToDelete) {
      deleteTemplateMutation.mutate({ templateId: templateToDelete._id });
      setShowDeleteDialog(false);
      setTemplateToDelete(null);
    }
  };

  // Memoized templates and pagination data
  const { templates, pagination } = useMemo(() => {
    if (!templatesData?.docs) {
      return { templates: [], pagination: null };
    }

    return {
      templates: templatesData.docs || [],
      pagination: {
        pageIndex: (templatesData.page || 1) - 1,
        pageSize: templatesData.limit || 10,
        totalPages: templatesData.totalPages || 0,
        total: templatesData.totalDocs || 0,
        hasPrevPage: templatesData.hasPrevPage || false,
        hasNextPage: templatesData.hasNextPage || false,
      },
    };
  }, [templatesData]);

  const StatusBadge = ({ status }) => {
    const getStatusIcon = () => {
      switch (status) {
        case 'APPROVED':
          return <CheckCircle className="w-3 h-3 mr-1" />;
        case 'REJECTED':
          return <AlertCircle className="w-3 h-3 mr-1" />;
        case 'PAUSED':
          return <Pause className="w-3 h-3 mr-1" />;
        default:
          return <Clock className="w-3 h-3 mr-1" />;
      }
    };

    const colorClass = STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';

    const statusLabel =
      status.charAt(0).toUpperCase() +
      status.slice(1).toLowerCase().replace('_', ' ');

    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${colorClass} shadow-sm border border-black/5`}
      >
        {getStatusIcon()}
        {statusLabel}
      </span>
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading templates
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {error.message || 'An unexpected error occurred'}
              </div>
              <div className="mt-4">
                <button
                  onClick={() => refetch()}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-white shadow flex-shrink-0">
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center">
                <Layout className="w-8 h-8 mr-3 text-primary-600" />
                Templates
              </h1>
              <p className="mt-1 text-sm text-gray-500 font-medium">
                Manage your WhatsApp message templates ({pagination?.total || 0}{' '}
                total)
              </p>
            </div>
            <Link
              to={`/projects/${projectId}/templates/create`}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Plus className="-ml-1 mr-2 h-5 w-5" />
              Create Template
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                value={filters.search}
                onChange={handleSearchChange}
                placeholder="Search templates..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>

            {/* Category Filter */}
            <div className="w-full md:w-48 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-4 w-4 text-gray-400" />
              </div>
              <select
                id="category"
                value={filters.category}
                onChange={e => handleFilterChange('category', e.target.value)}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm appearance-none"
              >
                <option value="">All Categories</option>
                <option value={TEMPLATE_CATEGORIES.AUTHENTICATION}>
                  Authentication
                </option>
                <option value={TEMPLATE_CATEGORIES.MARKETING}>Marketing</option>
                <option value={TEMPLATE_CATEGORIES.UTILITY}>Utility</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="w-full md:w-48 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-4 w-4 text-gray-400" />
              </div>
              <select
                id="status"
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm appearance-none"
              >
                <option value="">All Statuses</option>
                <option value={TEMPLATE_STATUSES.PENDING}>Pending</option>
                <option value={TEMPLATE_STATUSES.APPROVED}>Approved</option>
                <option value={TEMPLATE_STATUSES.REJECTED}>Rejected</option>
                <option value={TEMPLATE_STATUSES.PAUSED}>Paused</option>
                <option value={TEMPLATE_STATUSES.PENDING_DELETION}>
                  Pending Deletion
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Templates Table area */}
      <div className="flex-1 overflow-hidden flex flex-col bg-white">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#fcfcfd] sticky top-0 z-10 shadow-sm">
              <tr>
                <SortableHeader
                  columnId="name"
                  label="Template & Details"
                  sortBy={filters.sortBy}
                  sortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  columnId="category"
                  label="Category"
                  sortBy={filters.sortBy}
                  sortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  columnId="status"
                  label="Status"
                  sortBy={filters.sortBy}
                  sortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  columnId="language"
                  label="Language"
                  sortBy={filters.sortBy}
                  sortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  columnId="createdAt"
                  label="Created At"
                  sortBy={filters.sortBy}
                  sortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <th
                  scope="col"
                  className={`px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-widest bg-gray-50 cursor-pointe`}
                >
                  <div className="flex items-center gap-1">
                    <span>Manage</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      <span className="mt-2 text-sm">Loading templates...</span>
                    </div>
                  </td>
                </tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center">
                      <Layout className="w-12 h-12 text-gray-200 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900">
                        No templates found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 font-medium">
                        {filters.search || filters.category || filters.status
                          ? 'Try adjusting your filters or search'
                          : 'Get started by creating your first message template'}
                      </p>
                      {!filters.search &&
                        !filters.category &&
                        !filters.status && (
                          <Link
                            to={`/projects/${projectId}/templates/create`}
                            className="mt-6 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                          >
                            <Plus className="w-4 h-4 mr-2" /> Create Template
                          </Link>
                        )}
                    </div>
                  </td>
                </tr>
              ) : (
                templates.map(template => (
                  <tr
                    key={template._id}
                    className="hover:bg-gray-50/80 transition-colors group cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/projects/${projectId}/templates/${template._id}`
                      )
                    }
                  >
                    <td className="whitespace-nowrap px-6 py-5">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                          {template.name}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm border border-black/5 ${
                          CATEGORY_COLORS[template.category] ||
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {template.category.charAt(0).toUpperCase() +
                          template.category.slice(1).toLowerCase()}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <StatusBadge status={template.status} />
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <div className="flex items-center text-sm font-medium text-gray-700">
                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold mr-2 border border-gray-200">
                          {template.language?.split('_')[0].toUpperCase() ||
                            'EN'}
                        </span>
                        <span className="text-gray-900">
                          {template.language}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-5 text-[11px] font-medium text-gray-500">
                      {new Date(template.createdAt).toLocaleDateString(
                        undefined,
                        {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        }
                      )}
                    </td>
                    <td className="relative whitespace-nowrap px-6 py-5 text-right text-sm font-medium border-l border-gray-50 bg-gray-50/30 group-hover:bg-transparent transition-colors">
                      <div className="flex items-center space-x-4">
                        {(() => {
                          const editableStatuses = [
                            'APPROVED',
                            'REJECTED',
                            'PAUSED',
                          ];
                          const canEdit = editableStatuses.includes(
                            template.status
                          );

                          if (canEdit) {
                            return (
                              <Link
                                to={`/projects/${projectId}/templates/${template._id}/edit`}
                                className="text-indigo-600 hover:text-indigo-900 font-bold transition-colors"
                                onClick={e => e.stopPropagation()}
                              >
                                Edit
                              </Link>
                            );
                          } else {
                            return (
                              <div className="relative group/tooltip">
                                <span className="text-gray-400 cursor-not-allowed font-bold">
                                  Edit
                                </span>
                                <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-[10px] rounded shadow-xl opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                  {template.status === 'PENDING'
                                    ? 'Template is currently under review by Meta'
                                    : 'This template cannot be edited in its current status'}
                                  <div className="absolute top-full right-4 -mt-1">
                                    <div className="border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        })()}
                        <button
                          onClick={e => handleDeleteTemplate(e, template)}
                          className="text-rose-600 hover:text-rose-900 font-bold transition-colors"
                          disabled={deleteTemplateMutation.isPending}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Standard Pagination */}
      {!isLoading && templates.length > 0 && pagination && (
        <TablePagination
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Template"
        message={`Are you sure you want to delete the template "${templateToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={confirmDeleteTemplate}
        onCancel={() => {
          setShowDeleteDialog(false);
          setTemplateToDelete(null);
        }}
        isLoading={deleteTemplateMutation.isPending}
      />
    </div>
  );
};

export default TemplatesList;
